import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  ArrowLeft, 
  Rocket, 
  FileText, 
  Megaphone, 
  Settings, 
  BookOpen,
  Copy,
  Download,
  Loader2,
  CheckCircle2,
  Circle
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { ArchitectureDiagram } from "@/components/ArchitectureDiagram";
import { PromptBlock } from "@/components/PromptBlock";
import { DataModelDialog } from "@/components/DataModelDialog";

interface KPIChecklist {
  mvp_ready: boolean;
  domain_secured: boolean;
  auth_setup: boolean;
  database_setup: boolean;
  onboarding_flow: boolean;
  analytics_setup: boolean;
  email_setup: boolean;
  cron_setup: boolean;
  launch_checklist: boolean;
}

const IdeaBuilder = () => {
  const { ideaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [checklist, setChecklist] = useState<KPIChecklist>({
    mvp_ready: false,
    domain_secured: false,
    auth_setup: false,
    database_setup: false,
    onboarding_flow: false,
    analytics_setup: false,
    email_setup: false,
    cron_setup: false,
    launch_checklist: false,
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate("/auth");
    }
  }, [user, navigate]);

  // Fetch idea
  const { data: idea, isLoading: ideaLoading } = useQuery({
    queryKey: ["idea-builder", ideaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ideas")
        .select("*")
        .eq("id", ideaId)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error("Idea not found");
      return data;
    },
    enabled: !!ideaId && !!user,
  });

  // Fetch build plan
  const { data: buildPlan, isLoading: buildPlanLoading } = useQuery({
    queryKey: ["build-plan", ideaId, user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("build_plans")
        .select("*")
        .eq("idea_id", ideaId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!ideaId && !!user,
  });

  // Fetch tech specs
  const { data: techSpec, isLoading: techSpecLoading } = useQuery({
    queryKey: ["tech-spec", ideaId, user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("tech_specs")
        .select("*")
        .eq("idea_id", ideaId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!ideaId && !!user,
  });

  // Fetch marketing kit
  const { data: marketingKit, isLoading: marketingKitLoading } = useQuery({
    queryKey: ["marketing-kit", ideaId, user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("marketing_kits")
        .select("*")
        .eq("idea_id", ideaId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!ideaId && !!user,
  });

  // Load checklist from build plan
  useEffect(() => {
    if (buildPlan?.plan && typeof buildPlan.plan === 'object' && !Array.isArray(buildPlan.plan)) {
      const planData = buildPlan.plan as any;
      if (planData.meta?.checklist) {
        setChecklist(planData.meta.checklist);
      }
    }
  }, [buildPlan]);

  // Update checklist mutation
  const updateChecklistMutation = useMutation({
    mutationFn: async (newChecklist: KPIChecklist) => {
      if (!user || !ideaId) throw new Error("Not authenticated");

      const currentPlan = (buildPlan?.plan && typeof buildPlan.plan === 'object' && !Array.isArray(buildPlan.plan)) 
        ? buildPlan.plan as any 
        : {};

      const updatedPlan = {
        ...currentPlan,
        meta: {
          ...(currentPlan.meta || {}),
          checklist: newChecklist,
        },
      };

      if (buildPlan) {
        // Update existing
        const { error } = await supabase
          .from("build_plans")
          .update({ plan: updatedPlan as any })
          .eq("id", buildPlan.id);
        
        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from("build_plans")
          .insert([{
            user_id: user.id,
            idea_id: ideaId,
            plan: updatedPlan as any,
          }]);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["build-plan", ideaId, user?.id] });
    },
  });

  // Generate 90-day plan
  const generate90DayPlan = useMutation({
    mutationFn: async () => {
      if (!user || !ideaId) throw new Error("Not authenticated");

      // TODO: Call edge function to generate plan using AI
      const mockPlan = {
        phases: [
          {
            name: "Phase 1: Foundation (Days 1-30)",
            weeks: [
              { week: 1, tasks: ["Define MVP features", "Set up development environment", "Create wireframes"] },
              { week: 2, tasks: ["Build core authentication", "Set up database schema", "Implement basic UI"] },
              { week: 3, tasks: ["Develop key features", "Add user onboarding", "Set up analytics"] },
              { week: 4, tasks: ["Testing and bug fixes", "Performance optimization", "Documentation"] },
            ],
          },
          {
            name: "Phase 2: Growth (Days 31-60)",
            weeks: [
              { week: 5, tasks: ["Launch beta version", "Collect user feedback", "Marketing preparation"] },
              { week: 6, tasks: ["Iterate on feedback", "Add requested features", "Content creation"] },
              { week: 7, tasks: ["Marketing campaigns", "Community building", "Partnership outreach"] },
              { week: 8, tasks: ["Scale infrastructure", "Customer support setup", "Analytics review"] },
            ],
          },
          {
            name: "Phase 3: Scale (Days 61-90)",
            weeks: [
              { week: 9, tasks: ["Product refinement", "Advanced features", "Premium tier development"] },
              { week: 10, tasks: ["Sales funnel optimization", "Conversion tracking", "A/B testing"] },
              { week: 11, tasks: ["Team expansion", "Process documentation", "Automation setup"] },
              { week: 12, tasks: ["Launch v1.0", "PR campaign", "Growth acceleration"] },
            ],
          },
        ],
        meta: { checklist },
      };

      const { error } = await supabase
        .from("build_plans")
        .upsert([{
          user_id: user.id,
          idea_id: ideaId,
          plan: mockPlan as any,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["build-plan", ideaId, user?.id] });
      toast({
        title: "90-Day Plan Generated",
        description: "Your startup build plan is ready",
      });
    },
    onError: (error) => {
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate plan",
        variant: "destructive",
      });
    },
  });

  // Generate tech spec
  const generateTechSpec = useMutation({
    mutationFn: async () => {
      if (!user || !ideaId) throw new Error("Not authenticated");

      // Determine architecture based on idea characteristics
      const tags = idea?.tags || [];
      const niche = idea?.niche?.toLowerCase() || "";
      const isB2B = idea?.icp?.toLowerCase().includes('business') || 
                    idea?.icp?.toLowerCase().includes('enterprise');
      const isFintech = tags.some((t: string) => t.toLowerCase().includes('finance')) || 
                        niche.includes('fintech') || niche.includes('payment');
      const isMarketplace = tags.some((t: string) => t.toLowerCase().includes('marketplace')) || 
                           niche.includes('marketplace');
      const isContentPlatform = tags.some((t: string) => 
        ['content', 'media', 'social', 'ugc'].some(k => t.toLowerCase().includes(k))
      );

      // Build data model based on type
      const dataModel: any = {
        core: [
          { table: 'users', fields: ['id uuid PK', 'email text', 'created_at timestamptz'] },
          { table: 'profiles', fields: ['id uuid PK', 'user_id uuid FK', 'full_name text', 'avatar_url text'] },
        ],
        business: [],
      };

      if (isFintech) {
        dataModel.business.push(
          { table: 'accounts', fields: ['id uuid PK', 'user_id uuid FK', 'balance numeric', 'currency text'] },
          { table: 'transactions', fields: ['id uuid PK', 'account_id uuid FK', 'amount numeric', 'status text', 'created_at timestamptz'] }
        );
      }

      if (isMarketplace) {
        dataModel.business.push(
          { table: 'listings', fields: ['id uuid PK', 'seller_id uuid FK', 'title text', 'price numeric', 'status text'] },
          { table: 'orders', fields: ['id uuid PK', 'buyer_id uuid FK', 'listing_id uuid FK', 'status text', 'created_at timestamptz'] }
        );
      }

      if (isContentPlatform) {
        dataModel.business.push(
          { table: 'posts', fields: ['id uuid PK', 'author_id uuid FK', 'content text', 'media_urls text[]', 'created_at timestamptz'] },
          { table: 'comments', fields: ['id uuid PK', 'post_id uuid FK', 'user_id uuid FK', 'content text'] }
        );
      }

      // Generate integrations list
      const integrations = [
        'Supabase Auth (email/password + OAuth)',
        'Supabase Database (PostgreSQL)',
        'Supabase Storage (file uploads)',
      ];

      if (isFintech) {
        integrations.push('Stripe API (payments)', 'Plaid API (bank connections)');
      }

      if (isContentPlatform) {
        integrations.push('Cloudflare Images (CDN)', 'Moderation API (content safety)');
      }

      integrations.push(
        'Resend/SendGrid (transactional email)',
        'Supabase Edge Functions (serverless logic)',
        'Cron Jobs (scheduled tasks)'
      );

      // Generate routes and components
      const routesAndComponents = {
        pages: [
          '/ - Landing page with hero, features, pricing, CTA',
          '/auth - Sign in/sign up with email and OAuth',
          '/dashboard - User dashboard with key metrics and actions',
          '/build/[id] - Builder interface with tabs',
          ...(isMarketplace ? ['/listings - Browse all listings', '/listing/[id] - Listing detail'] : []),
          ...(isContentPlatform ? ['/feed - Content feed', '/post/[id] - Post detail'] : []),
        ],
        components: [
          'Navbar - Main navigation with auth state',
          'Footer - Links and branding',
          'AuthForm - Unified login/signup form',
          'PlanTimeline - Week-by-week plan visualization',
          'TaskCard - Individual task with status',
          'FavoriteButton - Heart icon toggle',
          'PromptBlock - Copyable prompt with syntax highlighting',
          ...(isMarketplace ? ['ListingCard', 'OrderSummary'] : []),
          ...(isContentPlatform ? ['PostCard', 'CommentThread'] : []),
        ],
        hooks: [
          'useFavorites() - Manage favorite ideas',
          'usePlan() - Fetch and update build plan',
          'useTechSpec() - Fetch tech specifications',
          'useMarketingKit() - Fetch marketing materials',
          'useAuth() - Authentication state and methods',
        ],
      };

      // Generate checklists
      const checklists = {
        environment: [
          'VITE_SUPABASE_URL',
          'VITE_SUPABASE_PUBLISHABLE_KEY',
          ...(isFintech ? ['STRIPE_SECRET_KEY', 'PLAID_CLIENT_ID'] : []),
          'RESEND_API_KEY',
        ],
        setup: [
          'Initialize Supabase project',
          'Set up database schema with RLS policies',
          'Configure authentication providers',
          'Set up storage buckets with policies',
          'Deploy edge functions',
          'Configure custom domain (optional)',
        ],
      };

      // Generate prompts
      const scaffoldPrompt = `Scaffold the following in React + TypeScript + Vite + Tailwind:

**Pages:**
${routesAndComponents.pages.map(p => `- ${p}`).join('\n')}

**Key Components:**
${routesAndComponents.components.map(c => `- ${c}`).join('\n')}

**Hooks:**
${routesAndComponents.hooks.map(h => `- ${h}`).join('\n')}

**Database Integration:**
- Use Supabase client for all database operations
- Implement RLS policies for security
- Use React Query for data fetching and caching

**Authentication:**
- Implement email/password authentication
- Add OAuth providers (Google, GitHub)
- Protected routes that redirect to /auth

**Styling:**
- Use Tailwind CSS with custom design tokens
- Responsive design (mobile-first)
- Dark mode support`;

      const chatgptPrompt = `Implement ${idea?.title} step-by-step:

**Context:**
${idea?.problem || 'Solving a key market problem'}
Target: ${idea?.icp || 'Target customers'}

**Implementation Steps:**

1. **Authentication Flow**
   - Set up Supabase Auth with email/password
   - Add OAuth providers (Google, GitHub)
   - Create protected route wrapper
   - Implement sign in/sign up forms with validation

2. **Database Schema**
   - Design tables with proper relationships
   - Add RLS policies for data security
   - Create database functions for complex queries
   - Set up real-time subscriptions where needed

3. **Core Features**
${isMarketplace ? `   - Listing creation and management
   - Search and filter functionality
   - Order processing workflow
   - Seller dashboard` : ''}
${isContentPlatform ? `   - Post creation with media upload
   - Comment system with threading
   - Content moderation
   - User feed with infinite scroll` : ''}
${isFintech ? `   - Account management
   - Transaction processing
   - Payment integration
   - Compliance checks` : ''}

4. **UI/UX Polish**
   - Responsive layouts for all screen sizes
   - Loading states and error handling
   - Toast notifications for user actions
   - Smooth animations and transitions

5. **Testing & Deployment**
   - Test all user flows
   - Check RLS policies
   - Deploy to production
   - Set up monitoring`;

      const migrationPrompt = `-- ${idea?.title} Database Schema

-- Enable necessary extensions
CREATE EXTENSION IF NOT EXISTS "uuid-ossp";

-- Core tables
${dataModel.core.map((t: any) => `
CREATE TABLE ${t.table} (
  ${t.fields.join(',\n  ')}
);

ALTER TABLE ${t.table} ENABLE ROW LEVEL SECURITY;

CREATE POLICY "${t.table}_policy" ON ${t.table}
  FOR ALL USING (auth.uid() = user_id);
`).join('\n')}

-- Business tables
${dataModel.business.map((t: any) => `
CREATE TABLE ${t.table} (
  ${t.fields.join(',\n  ')}
);

ALTER TABLE ${t.table} ENABLE ROW LEVEL SECURITY;

CREATE POLICY "${t.table}_policy" ON ${t.table}
  FOR ALL USING (auth.uid() = user_id);
`).join('\n')}

-- Indexes for performance
${[...dataModel.core, ...dataModel.business].map((t: any) => 
  `CREATE INDEX idx_${t.table}_user_id ON ${t.table}(user_id);`
).join('\n')}

-- Updated_at trigger
CREATE OR REPLACE FUNCTION update_updated_at()
RETURNS TRIGGER AS $$
BEGIN
  NEW.updated_at = now();
  RETURN NEW;
END;
$$ LANGUAGE plpgsql;

${[...dataModel.core, ...dataModel.business].map((t: any) => 
  `CREATE TRIGGER ${t.table}_updated_at
    BEFORE UPDATE ON ${t.table}
    FOR EACH ROW
    EXECUTE FUNCTION update_updated_at();`
).join('\n')}`;

      const mockSpec = {
        architecture: {
          frontend: "React + TypeScript + Vite",
          backend: "Supabase (PostgreSQL + Edge Functions)",
          hosting: "Vercel / Netlify",
          auth: "Supabase Auth (Email + OAuth)",
          database: "PostgreSQL via Supabase",
          storage: "Supabase Storage",
          queue: "Supabase Edge Functions + pg_cron",
        },
        data_model: dataModel,
        integrations,
        routes_and_components: routesAndComponents,
        checklists,
        prompts: {
          scaffold: scaffoldPrompt,
          chatgpt: chatgptPrompt,
          migration: migrationPrompt,
        },
      };

      const { error } = await supabase
        .from("tech_specs")
        .upsert([{
          user_id: user.id,
          idea_id: ideaId,
          spec: mockSpec as any,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tech-spec", ideaId, user?.id] });
      toast({
        title: "Tech Spec Generated",
        description: "Your technical specifications are ready",
      });
    },
    onError: (error) => {
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate tech spec",
        variant: "destructive",
      });
    },
  });

  // Generate marketing kit
  const generateMarketingKit = useMutation({
    mutationFn: async () => {
      if (!user || !ideaId) throw new Error("Not authenticated");

      // Analyze idea characteristics
      const tags = idea?.tags || [];
      const niche = idea?.niche?.toLowerCase() || "";
      const isB2B = idea?.icp?.toLowerCase().includes('business') || 
                    idea?.icp?.toLowerCase().includes('enterprise');
      const isB2C = !isB2B;
      const isFintech = tags.some((t: string) => t.toLowerCase().includes('finance')) || 
                        niche.includes('fintech') || niche.includes('payment');
      const isMarketplace = tags.some((t: string) => t.toLowerCase().includes('marketplace')) || 
                           niche.includes('marketplace');
      const isContentPlatform = tags.some((t: string) => 
        ['content', 'media', 'social', 'ugc'].some(k => t.toLowerCase().includes(k))
      );

      // Build comprehensive ICP
      const primaryICP = idea?.icp || (isB2B ? "Mid-market companies (50-500 employees)" : "Tech-savvy individuals aged 25-45");
      const secondaryICP = isB2B ? "Enterprise teams (500+ employees)" : "Early adopters and power users";
      
      const painPoints = isFintech 
        ? ["Manual financial processes", "Lack of real-time insights", "Complex compliance requirements", "High transaction fees"]
        : isMarketplace
        ? ["Difficulty finding trusted providers", "Opaque pricing", "Poor user experience", "Limited payment options"]
        : isContentPlatform
        ? ["Content moderation challenges", "Low engagement rates", "Monetization difficulties", "Platform dependence"]
        : ["Inefficient workflows", "High operational costs", "Poor user experience", "Scaling challenges"];

      const buyingTriggers = isB2B 
        ? ["Budget approval cycles", "Quarterly planning", "Team expansion", "Tool consolidation initiatives"]
        : ["Product launches", "Viral social proof", "Seasonal events", "Competitive pricing"];

      // Generate 10 hooks
      const problem = idea?.problem || "common pain point";
      const solution = idea?.solution || "our solution";
      const category = idea?.niche || "tool";
      
      const hooks = [
        `Stop wasting ${isB2B ? '$10K/month' : '15 hours/week'} on ${problem}`,
        `The ${category} that ${isB2B ? 'Fortune 500s' : 'everyone'} is switching to`,
        `${isB2B ? 'Ship faster' : 'Get results'} without ${isB2B ? 'hiring a team' : 'the learning curve'}`,
        `What if ${problem} could be solved in ${isB2B ? 'one dashboard' : '3 clicks'}?`,
        `Built by ${isB2B ? 'ex-Google engineers' : 'frustrated users'} tired of ${problem}`,
        `${isB2C ? 'Free to start' : 'ROI in 30 days'}, guaranteed or your money back`,
        `Join ${isB2C ? '10,000+' : '500+'} ${isB2B ? 'companies' : 'users'} already saving time`,
        `The ${category} designed for ${isB2B ? 'scale' : 'simplicity'}`,
        `Why ${isB2B ? 'leading teams' : 'smart people'} are ditching [competitor] for us`,
        `${solution} meets ${category} - finally done right`
      ];

      // Positioning
      const positioning = {
        value_prop: `${solution} for ${primaryICP} who want to ${isB2B ? 'increase efficiency and reduce costs' : 'save time and get better results'}`,
        category: category,
        alternatives: isB2B 
          ? ["Enterprise legacy tools (complex, expensive)", "In-house solutions (time-consuming)", "Consulting firms ($$$$)"]
          : ["Manual processes (slow)", "Spreadsheets (error-prone)", "Competitor tools (missing features)"]
      };

      // Landing page structure
      const landing_page = {
        headline: `${solution} - ${isB2B ? 'Built for Teams That Ship' : 'Simple. Powerful. Yours.'}`,
        subheads: [
          `Solve ${problem} in minutes, not ${isB2B ? 'months' : 'days'}`,
          `${isB2B ? 'Trusted by leading companies' : 'Join thousands of happy users'}`
        ],
        sections: [
          { title: "Problem", content: `${problem} costs you ${isB2B ? 'revenue and time' : 'time and frustration'}` },
          { title: "Solution", content: `${solution} - the ${category} that actually works` },
          { title: "How It Works", content: "1. Sign up in 60 seconds 2. [Key action] 3. See results instantly" },
          { title: "Features", content: "✓ [Feature 1] ✓ [Feature 2] ✓ [Feature 3]" },
          { title: "Social Proof", content: `${isB2C ? '10,000+ users' : '500+ companies'} | 4.9★ rating | Featured in [Publication]` },
          { title: "Pricing", content: isB2C ? "Free to start, upgrade anytime" : "Starting at $X/month per user" }
        ],
        faqs: [
          { q: "How is this different from [competitor]?", a: "We focus on [unique value] while they don't." },
          { q: "How long does setup take?", a: isB2B ? "Under 5 minutes with SSO" : "Less than 60 seconds" },
          { q: "Do you offer support?", a: isB2B ? "24/7 dedicated support + CSM" : "Email support + comprehensive docs" },
          { q: "Can I cancel anytime?", a: "Yes, no long-term contracts. Cancel in one click." }
        ],
        cta: isB2C ? "Start Free Trial" : "Book a Demo"
      };

      // Ad creatives with detailed scripts
      const ad_creatives = {
        tiktok_ig_ugc: [
          {
            duration: "15s",
            script: "Hook (0-3s): 'I used to spend [time] on [pain]...' → Problem (3-8s): Show frustration → Solution (8-12s): Quick demo → CTA (12-15s): 'Link in bio - try free'",
            shot_list: ["Close-up frustrated face", "Screen record of problem", "Screen record of solution", "Satisfied smile + CTA text overlay"]
          },
          {
            duration: "30s",
            script: "Hook (0-3s): 'Stop me if you've heard this...' → Story (3-15s): Relatable pain story → Demo (15-25s): Show solution in action → CTA (25-30s): 'Comment LINK for access'",
            shot_list: ["Talking head intro", "B-roll of problem", "Clean screen demo", "Medium shot with URL", "Text overlay CTA"]
          },
          {
            duration: "45s",
            script: "Hook (0-5s): Shocking stat about problem → Personal Story (5-20s): 'Here's what changed...' → Feature Walkthrough (20-35s): 3 key features → Social Proof (35-40s): Results/testimonials → CTA (40-45s): Special offer",
            shot_list: ["Text stat animation", "Personal story talking head", "Feature demo 1-3", "Screenshot of testimonial", "Offer + URL overlay", "Thumbs up"]
          }
        ],
        reddit: {
          entrepreneur: {
            title: `[Feedback] Built ${idea?.title} to solve ${problem} - what am I missing?`,
            body: `Hey r/Entrepreneur,\n\nSpent the last [X months] building a solution for [problem]. Background: I'm a [profession] who got frustrated with [pain].\n\n**What it does:** ${solution}\n\n**Current traction:** [X users/MRR/whatever you have]\n\n**Looking for:** Honest feedback on the idea, positioning, and what you'd want to see before trying it.\n\nHappy to answer questions. Not here to spam - genuinely want to build something useful.\n\n[Only include link if asked in comments]`
          },
          niche_subs: {
            title: `How do you currently handle ${problem}?`,
            body: `Curious what tools/processes the community uses for [pain]. \n\nI've been using [current solution] but finding [specific limitation].\n\nWondering if anyone has found something better? \n\n[Contribute to discussion first, only mention your tool naturally if relevant]`
          }
        },
        product_hunt: {
          "T-7": "Finalize tagline, screenshots, demo video. Build email list (Twitter, LinkedIn teasers)",
          "T-5": "Schedule launch for Tuesday-Thursday 12:01 AM PST. Prepare 'First Comment' with detailed description",
          "T-3": "Reach out to maker community for support. Prepare hunter outreach if applicable",
          "T-1": "Final testing. Prepare response templates. Set up monitoring dashboard",
          "T-0": "Launch at 12:01 AM PST. Post first comment. Share on socials. Monitor comments/questions",
          "T+1": "Respond to all comments. Thank supporters. Share milestones",
          "T+2": "Analyze results. Follow up with engaged users. Collect feedback",
          "T+3": "Post-launch recap. Email list with results. Plan iteration based on feedback"
        }
      };

      // 4-week content calendar
      const content_calendar = [
        { week: 1, posts: [
          { day: "Mon", type: "Educational", brief: `How to [solve problem] - comprehensive guide with [your tool] as one solution` },
          { day: "Wed", type: "Behind-the-scenes", brief: "Building in public: Why we started this and what we learned" },
          { day: "Fri", type: "Quick tip", brief: `One weird trick to [related benefit] that nobody talks about` }
        ]},
        { week: 2, posts: [
          { day: "Mon", type: "Case study", brief: "How [user/company] achieved [result] in [timeframe]" },
          { day: "Wed", type: "Comparison", brief: `${idea?.title} vs [competitor]: honest breakdown` },
          { day: "Fri", type: "Feature spotlight", brief: "Deep dive: [Key feature] and how it saves you [benefit]" }
        ]},
        { week: 3, posts: [
          { day: "Mon", type: "User testimonial", brief: "Video/written testimonial from satisfied user" },
          { day: "Wed", type: "Industry insight", brief: `[Stat/trend] in ${niche} - what it means for you` },
          { day: "Fri", type: "Community", brief: "Q&A: Your questions about [topic] answered" }
        ]},
        { week: 4, posts: [
          { day: "Mon", type: "Milestone", brief: "We hit [X users/milestone] - here's what we learned" },
          { day: "Wed", type: "Future vision", brief: "Roadmap sneak peek: What's coming next" },
          { day: "Fri", type: "Value delivery", brief: "Free template/checklist/tool related to [pain]" }
        ]}
      ];

      // KPIs
      const kpis = isB2B ? {
        signups: { target: "50 qualified leads/month", metric: "Month 1: Focus on hand-picked outreach" },
        ctr: { target: "2-5% on LinkedIn ads", metric: "A/B test messaging variations" },
        cpl: { target: "$50-100 per qualified lead", metric: "Optimize for decision-maker titles" },
        activation: { target: "30% trial-to-paid", metric: "Onboarding call + demo required" },
        expansion: { target: "20% MoM growth", metric: "Track referrals, upsells, and retention" }
      } : {
        signups: { target: "500 signups/month", metric: "Organic + paid social mix" },
        ctr: { target: "3-8% on TikTok/IG ads", metric: "Test 5+ hook variations per week" },
        cpl: { target: "$5-15 per signup", metric: "Optimize for mobile conversion" },
        activation: { target: "40% signup-to-active user", metric: "First action within 24 hours" },
        viral_coefficient: { target: "0.3+ (30% of users invite 1 friend)", metric: "Built-in sharing incentives" }
      };

      // Prompts for AI generation
      const prompts = {
        tiktok_scripts: `Generate 5 TikTok/IG Reels scripts for ${idea?.title}:

**Context:**
- Product: ${solution}
- Target: ${primaryICP}
- Pain: ${problem}
- Hooks: ${hooks.slice(0, 3).join(', ')}

**Requirements:**
- 15-30 second scripts
- Start with pattern interrupt hook (first 3 seconds critical)
- Show problem → solution → CTA structure
- Include shot list for creator
- Optimize for mobile viewing (text overlays)
- Trendy but not cringe

**Tone:** ${isB2B ? 'Professional but conversational' : 'Authentic, relatable, fast-paced'}`,

        landing_page: `Write complete landing page copy for ${idea?.title}:

**Hero Section:**
- Headline: Clear value prop that includes ${problem}
- Subheadline: Supporting benefit statement
- CTA: ${landing_page.cta}
- Visual: ${isB2B ? 'Dashboard screenshot' : 'Product in action'}

**Problem Section (150 words):**
Paint the pain: ${problem}

**Solution Section (200 words):**
How we solve it: ${solution}

**How It Works (3 steps):**
Make it simple and visual

**Features Section:**
3-5 key features with benefit-focused descriptions

**Social Proof:**
${isB2C ? 'User testimonials + usage stats' : 'Company logos + case study snippets'}

**Pricing:**
${isB2C ? 'Free tier + paid tiers' : 'Custom pricing with "Book Demo" CTA'}

**FAQ (5-7 questions):**
Address objections

**Final CTA:**
Drive conversion`,

        product_hunt: `Create Product Hunt launch package for ${idea?.title}:

**Tagline (60 chars):**
[Clear, benefit-driven one-liner]

**Description (260 chars):**
[What it does + who it's for + key benefit]

**First Comment (detailed):**
👋 Hey Product Hunt!

[Personal intro - who you are, why you built this]

**The Problem:**
${problem}

**Our Solution:**
${solution}

**Key Features:**
✅ [Feature 1]
✅ [Feature 2]
✅ [Feature 3]

**What's Next:**
[Roadmap tease]

**Special PH Offer:**
${isB2C ? 'Lifetime deal for first 100 hunters' : 'Extended trial for PH community'}

Would love your feedback! 🙏

**Maker Response Templates:**
- Feature request: "Great idea! We'll add to roadmap..."
- Comparison question: "vs [competitor]..."
- Pricing question: "Here's how we think about pricing..."`,

        reddit_seeding: `Write non-spammy Reddit posts for ${idea?.title}:

**r/Entrepreneur Post:**
[Story-driven, ask for feedback, provide value first]

**Niche Subreddit Post (r/${niche}):**
[Ask genuine question about ${problem}, contribute to discussion]

**Key Rules:**
- Provide value first
- Don't link unless asked
- Participate in comments
- Be transparent if asked about your solution
- Focus on helping, not selling`
      };

      const fullKit = {
        icp: {
          primary: primaryICP,
          secondary: secondaryICP,
          painPoints: painPoints,
          buyingTriggers: buyingTriggers
        },
        positioning,
        hooks,
        landing_page,
        ad_creatives,
        content_calendar,
        kpis,
        prompts
      };

      const { error } = await supabase
        .from("marketing_kits")
        .upsert([{
          user_id: user.id,
          idea_id: ideaId,
          kit: fullKit,
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["marketing-kit", ideaId, user?.id] });
      toast({
        title: "Marketing Kit Generated",
        description: "Your marketing materials are ready",
      });
    },
    onError: (error) => {
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate marketing kit",
        variant: "destructive",
      });
    },
  });

  const handleChecklistChange = (key: keyof KPIChecklist, value: boolean) => {
    const newChecklist = { ...checklist, [key]: value };
    setChecklist(newChecklist);
    updateChecklistMutation.mutate(newChecklist);
  };

  const copyToClipboard = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({ title: "Copied to clipboard" });
  };

  const exportAsMarkdown = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported successfully" });
  };

  const isLoading = ideaLoading || buildPlanLoading || techSpecLoading || marketingKitLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Idea not found</h2>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const checklistItems = [
    { key: "mvp_ready" as keyof KPIChecklist, label: "MVP Ready" },
    { key: "domain_secured" as keyof KPIChecklist, label: "Domain Secured" },
    { key: "auth_setup" as keyof KPIChecklist, label: "Authentication Setup" },
    { key: "database_setup" as keyof KPIChecklist, label: "Database Setup" },
    { key: "onboarding_flow" as keyof KPIChecklist, label: "Onboarding Flow" },
    { key: "analytics_setup" as keyof KPIChecklist, label: "Analytics Setup" },
    { key: "email_setup" as keyof KPIChecklist, label: "Email Setup" },
    { key: "cron_setup" as keyof KPIChecklist, label: "Cron Jobs Setup" },
    { key: "launch_checklist" as keyof KPIChecklist, label: "Launch Checklist Complete" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container-responsive max-w-7xl mx-auto py-6">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/idea/${ideaId}`)}
            className="mb-4 gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Idea
          </Button>

          <div className="space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold">{idea.title}</h1>
            <div className="flex flex-wrap gap-2">
              {idea.tags?.slice(0, 5).map((tag: string) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        {/* Sticky Action Bar */}
        <Card className="mb-6 sticky top-0 z-10 shadow-lg">
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => generate90DayPlan.mutate()}
                disabled={generate90DayPlan.isPending}
                className="gap-2"
              >
                {generate90DayPlan.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Rocket className="h-4 w-4" />
                )}
                Generate 90-Day Plan
              </Button>
              <Button
                onClick={() => generateTechSpec.mutate()}
                disabled={generateTechSpec.isPending}
                variant="outline"
                className="gap-2"
              >
                {generateTechSpec.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Generate Tech Spec
              </Button>
              <Button
                onClick={() => generateMarketingKit.mutate()}
                disabled={generateMarketingKit.isPending}
                variant="outline"
                className="gap-2"
              >
                {generateMarketingKit.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Megaphone className="h-4 w-4" />
                )}
                Generate Marketing Kit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="w-full justify-start overflow-x-auto">
            <TabsTrigger value="overview" className="gap-2">
              <Rocket className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="90day" className="gap-2">
              <FileText className="h-4 w-4" />
              90-Day Plan
            </TabsTrigger>
            <TabsTrigger value="tech" className="gap-2">
              <Settings className="h-4 w-4" />
              Technical Build
            </TabsTrigger>
            <TabsTrigger value="marketing" className="gap-2">
              <Megaphone className="h-4 w-4" />
              Marketing Engine
            </TabsTrigger>
            <TabsTrigger value="operations" className="gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Operations & Launch
            </TabsTrigger>
            <TabsTrigger value="resources" className="gap-2">
              <BookOpen className="h-4 w-4" />
              Resources
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Idea Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>Idea Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Problem</h4>
                    <p className="text-muted-foreground">{idea.problem || "Not yet defined"}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-semibold mb-2">Solution</h4>
                    <p className="text-muted-foreground">{idea.solution || "Not yet defined"}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-semibold mb-2">Target Customer</h4>
                    <p className="text-muted-foreground">{idea.icp || idea.target_customer || "Not yet defined"}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Right: KPI Checklist */}
              <Card>
                <CardHeader>
                  <CardTitle>Launch Checklist</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {checklistItems.map((item) => (
                      <div key={item.key} className="flex items-center gap-3">
                        <Checkbox
                          checked={checklist[item.key]}
                          onCheckedChange={(checked) => 
                            handleChecklistChange(item.key, checked as boolean)
                          }
                          id={item.key}
                        />
                        <label
                          htmlFor={item.key}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {item.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <Separator className="my-4" />
                  <div className="text-sm text-muted-foreground">
                    {Object.values(checklist).filter(Boolean).length} of {checklistItems.length} complete
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* 90-Day Plan Tab */}
          <TabsContent value="90day" className="space-y-4">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(JSON.stringify(buildPlan?.plan, null, 2))}
                className="gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportAsMarkdown(JSON.stringify(buildPlan?.plan, null, 2), "90-day-plan.md")}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export .md
              </Button>
            </div>

            {buildPlan?.plan && typeof buildPlan.plan === 'object' && !Array.isArray(buildPlan.plan) && (buildPlan.plan as any).phases ? (
              <div className="space-y-6">
                {(buildPlan.plan as any).phases.map((phase: any, idx: number) => (
                  <Card key={idx}>
                    <CardHeader>
                      <CardTitle>{phase.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {phase.weeks.map((week: any, wIdx: number) => (
                          <div key={wIdx}>
                            <h4 className="font-semibold mb-2">Week {week.week}</h4>
                            <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                              {week.tasks.map((task: string, tIdx: number) => (
                                <li key={tIdx}>{task}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Rocket className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No 90-Day Plan Yet</h3>
                  <p className="text-muted-foreground mb-4">Generate your startup build plan to get started</p>
                  <Button onClick={() => generate90DayPlan.mutate()} disabled={generate90DayPlan.isPending}>
                    Generate Plan
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Technical Build Tab */}
          <TabsContent value="tech" className="space-y-6">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(JSON.stringify(techSpec?.spec, null, 2))}
                className="gap-2"
              >
                <Copy className="h-4 w-4" />
                Copy All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportAsMarkdown(JSON.stringify(techSpec?.spec, null, 2), "tech-spec.md")}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export .md
              </Button>
              {techSpec?.spec && typeof techSpec.spec === 'object' && !Array.isArray(techSpec.spec) && (techSpec.spec as any).prompts?.migration && (
                <DataModelDialog sql={(techSpec.spec as any).prompts.migration} />
              )}
            </div>

            {techSpec?.spec && typeof techSpec.spec === 'object' && !Array.isArray(techSpec.spec) ? (
              <div className="space-y-6">
                {/* Architecture Diagram */}
                <ArchitectureDiagram architecture={(techSpec.spec as any).architecture || {}} />

                {/* Prompt Sections */}
                {(techSpec.spec as any).prompts && (
                  <div className="space-y-4">
                    <PromptBlock
                      title="App Scaffold Prompt"
                      description="Use this prompt to scaffold your entire application structure"
                      prompt={(techSpec.spec as any).prompts.scaffold}
                    />

                    <PromptBlock
                      title="ChatGPT Implementation Prompt"
                      description="Step-by-step guide for implementing complex features"
                      prompt={(techSpec.spec as any).prompts.chatgpt}
                    />
                  </div>
                )}

                {/* Architecture Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Tech Stack Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-3">
                      {Object.entries((techSpec.spec as any).architecture || {}).map(([key, value]) => (
                        <div key={key} className="flex gap-3 pb-3 border-b border-border/50 last:border-0">
                          <dt className="font-semibold capitalize min-w-[120px]">{key.replace('_', ' ')}:</dt>
                          <dd className="text-muted-foreground">{value as string}</dd>
                        </div>
                      ))}
                    </dl>
                  </CardContent>
                </Card>

                {/* Integrations */}
                {(techSpec.spec as any).integrations && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Required Integrations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {(techSpec.spec as any).integrations.map((integration: string, idx: number) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-primary" />
                            <span>{integration}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Routes & Components */}
                {(techSpec.spec as any).routes_and_components && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Pages & Components</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-3">Pages</h4>
                        <ul className="space-y-2 text-sm">
                          {(techSpec.spec as any).routes_and_components.pages?.map((page: string, idx: number) => (
                            <li key={idx} className="text-muted-foreground">{page}</li>
                          ))}
                        </ul>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-3">Key Components</h4>
                        <div className="flex flex-wrap gap-2">
                          {(techSpec.spec as any).routes_and_components.components?.map((comp: string, idx: number) => (
                            <Badge key={idx} variant="secondary">{comp}</Badge>
                          ))}
                        </div>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-3">Custom Hooks</h4>
                        <ul className="space-y-1 text-sm">
                          {(techSpec.spec as any).routes_and_components.hooks?.map((hook: string, idx: number) => (
                            <li key={idx} className="text-muted-foreground font-mono">{hook}</li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Checklists */}
                {(techSpec.spec as any).checklists && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Setup Checklist</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-3">Environment Variables</h4>
                        <div className="space-y-2">
                          {(techSpec.spec as any).checklists.environment?.map((env: string, idx: number) => (
                            <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded">
                              <Circle className="h-3 w-3 text-muted-foreground" />
                              <code className="text-sm">{env}</code>
                            </div>
                          ))}
                        </div>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-3">Setup Steps</h4>
                        <ol className="space-y-2 list-decimal list-inside text-sm text-muted-foreground">
                          {(techSpec.spec as any).checklists.setup?.map((step: string, idx: number) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Settings className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No Tech Spec Yet</h3>
                  <p className="text-muted-foreground mb-4">Generate your technical specifications</p>
                  <Button onClick={() => generateTechSpec.mutate()} disabled={generateTechSpec.isPending}>
                    Generate Tech Spec
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Marketing Engine Tab */}
          <TabsContent value="marketing" className="space-y-4">
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const kit = marketingKit?.kit as any;
                  const markdown = `# Marketing Kit - ${idea?.title}

## ICP & Positioning

**Primary ICP:** ${kit?.icp?.primary || 'N/A'}
**Secondary ICP:** ${kit?.icp?.secondary || 'N/A'}

**Pain Points:**
${kit?.icp?.painPoints?.map((p: string) => `- ${p}`).join('\n') || ''}

**Positioning:** ${kit?.positioning?.value_prop || 'N/A'}

## Hooks
${kit?.hooks?.map((h: string, i: number) => `${i + 1}. ${h}`).join('\n') || ''}

## Landing Page Copy
**Headline:** ${kit?.landing_page?.headline || ''}
**Subheads:** ${kit?.landing_page?.subheads?.join(' | ') || ''}

## Ad Creatives

### TikTok/IG Scripts
${kit?.ad_creatives?.tiktok_ig_ugc?.map((s: any) => `**${s.duration}:** ${s.script}`).join('\n\n') || ''}

### Reddit Strategy
${kit?.ad_creatives?.reddit ? JSON.stringify(kit.ad_creatives.reddit, null, 2) : ''}

## Content Calendar
${kit?.content_calendar?.map((w: any) => `**Week ${w.week}:** ${w.posts?.map((p: any) => `${p.day} - ${p.type}`).join(', ')}`).join('\n') || ''}

## KPIs
${kit?.kpis ? Object.entries(kit.kpis).map(([k, v]: [string, any]) => `**${k}:** ${v.target}`).join('\n') : ''}
`;
                  exportAsMarkdown(markdown, "marketing-kit.md");
                }}
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                Export All as .md
              </Button>
            </div>

            {marketingKit?.kit && typeof marketingKit.kit === 'object' && !Array.isArray(marketingKit.kit) ? (
              <div className="space-y-6">
                {/* ICP & Positioning */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Ideal Customer Profile</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Primary ICP</h4>
                        <p className="text-sm text-muted-foreground">{(marketingKit.kit as any).icp?.primary}</p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Secondary ICP</h4>
                        <p className="text-sm text-muted-foreground">{(marketingKit.kit as any).icp?.secondary}</p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Pain Points</h4>
                        <ul className="space-y-1 text-sm">
                          {(marketingKit.kit as any).icp?.painPoints?.map((pain: string, idx: number) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span className="text-muted-foreground">{pain}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Buying Triggers</h4>
                        <ul className="space-y-1 text-sm">
                          {(marketingKit.kit as any).icp?.buyingTriggers?.map((trigger: string, idx: number) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-primary mt-0.5">•</span>
                              <span className="text-muted-foreground">{trigger}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Positioning</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Value Proposition</h4>
                        <p className="text-sm text-muted-foreground">{(marketingKit.kit as any).positioning?.value_prop}</p>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Category</h4>
                        <Badge variant="secondary">{(marketingKit.kit as any).positioning?.category}</Badge>
                      </div>
                      <Separator />
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Alternatives</h4>
                        <ul className="space-y-1 text-sm">
                          {(marketingKit.kit as any).positioning?.alternatives?.map((alt: string, idx: number) => (
                            <li key={idx} className="flex items-start gap-2">
                              <span className="text-muted-foreground/50 mt-0.5">vs</span>
                              <span className="text-muted-foreground">{alt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Marketing Hooks */}
                <Card>
                  <CardHeader>
                    <CardTitle>10 Marketing Hooks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(marketingKit.kit as any).hooks?.map((hook: string, idx: number) => (
                        <div key={idx} className="p-3 bg-muted rounded-lg">
                          <span className="text-xs font-mono text-primary mr-2">#{idx + 1}</span>
                          <span className="text-sm">{hook}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Copyable Prompts */}
                {(marketingKit.kit as any).prompts && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">AI Prompts - Copy & Use</h3>
                    
                    <PromptBlock
                      title="Make 5 TikTok Scripts"
                      description="Generate viral short-form video scripts optimized for TikTok and Instagram Reels"
                      prompt={(marketingKit.kit as any).prompts.tiktok_scripts}
                    />

                    <PromptBlock
                      title="Landing Page Copy"
                      description="Complete landing page copy with all sections, CTAs, and conversion-optimized messaging"
                      prompt={(marketingKit.kit as any).prompts.landing_page}
                    />

                    <PromptBlock
                      title="Product Hunt Launch Checklist"
                      description="Full PH launch strategy with timeline, content, and community engagement plan"
                      prompt={(marketingKit.kit as any).prompts.product_hunt}
                    />

                    <PromptBlock
                      title="Reddit Seeding Post"
                      description="Non-spammy Reddit posts that provide value first and build authentic community engagement"
                      prompt={(marketingKit.kit as any).prompts.reddit_seeding}
                    />
                  </div>
                )}

                {/* Ad Creative Details */}
                {(marketingKit.kit as any).ad_creatives && (
                  <Card>
                    <CardHeader>
                      <CardTitle>TikTok/IG UGC Scripts</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {(marketingKit.kit as any).ad_creatives.tiktok_ig_ugc?.map((script: any, idx: number) => (
                        <div key={idx} className="p-4 border rounded-lg space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold">{script.duration} Version</h4>
                            <Badge variant="outline">{script.duration}</Badge>
                          </div>
                          <div>
                            <p className="text-sm font-medium mb-2">Script:</p>
                            <p className="text-sm text-muted-foreground">{script.script}</p>
                          </div>
                          <Separator />
                          <div>
                            <p className="text-sm font-medium mb-2">Shot List:</p>
                            <ul className="space-y-1">
                              {script.shot_list?.map((shot: string, sIdx: number) => (
                                <li key={sIdx} className="text-sm text-muted-foreground flex items-start gap-2">
                                  <span className="text-primary mt-0.5">{sIdx + 1}.</span>
                                  {shot}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Product Hunt Timeline */}
                {(marketingKit.kit as any).ad_creatives?.product_hunt && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Product Hunt Launch Timeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries((marketingKit.kit as any).ad_creatives.product_hunt).map(([day, task]: [string, any]) => (
                          <div key={day} className="flex gap-4 pb-3 border-b border-border/50 last:border-0">
                            <div className="font-mono text-sm font-semibold min-w-[60px] text-primary">{day}</div>
                            <div className="text-sm text-muted-foreground flex-1">{task}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Content Calendar */}
                {(marketingKit.kit as any).content_calendar && (
                  <Card>
                    <CardHeader>
                      <CardTitle>4-Week Content Calendar</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {(marketingKit.kit as any).content_calendar.map((week: any, wIdx: number) => (
                          <div key={wIdx} className="space-y-3">
                            <h4 className="font-semibold">Week {week.week}</h4>
                            <div className="space-y-2">
                              {week.posts?.map((post: any, pIdx: number) => (
                                <div key={pIdx} className="p-3 bg-muted rounded-lg">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge variant="outline" className="text-xs">{post.day}</Badge>
                                    <Badge variant="secondary" className="text-xs">{post.type}</Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">{post.brief}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* KPIs */}
                {(marketingKit.kit as any).kpis && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Marketing KPIs & Targets</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries((marketingKit.kit as any).kpis).map(([key, value]: [string, any]) => (
                          <div key={key} className="p-4 border rounded-lg">
                            <h4 className="font-semibold capitalize mb-2">{key.replace('_', ' ')}</h4>
                            <p className="text-sm text-muted-foreground mb-1">
                              <span className="font-medium">Target:</span> {value.target}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              <span className="font-medium">Metric:</span> {value.metric}
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <Megaphone className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No Marketing Kit Yet</h3>
                  <p className="text-muted-foreground mb-4">Generate your marketing materials</p>
                  <Button onClick={() => generateMarketingKit.mutate()} disabled={generateMarketingKit.isPending}>
                    Generate Marketing Kit
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Operations & Launch Tab */}
          <TabsContent value="operations" className="space-y-6">
            {/* Launch Checklist */}
            <Card>
              <CardHeader>
                <CardTitle>Launch Checklist</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { label: "Domain registered and DNS configured", key: "domain" },
                    { label: "Analytics setup (Google Analytics / Plausible)", key: "analytics" },
                    { label: "Privacy Policy & Terms of Service published", key: "legal" },
                    { label: "Support inbox configured (email/chat)", key: "support" },
                    { label: "Status page setup (status.domain.com)", key: "status" },
                    { label: "Automated backups configured", key: "backups" },
                    { label: "Error monitoring (Sentry / LogRocket)", key: "monitoring" },
                    { label: "SSL certificate active", key: "ssl" },
                    { label: "CDN configured for assets", key: "cdn" },
                    { label: "Performance testing completed", key: "performance" }
                  ].map((item) => (
                    <div key={item.key} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <Checkbox id={`launch-${item.key}`} />
                      <label
                        htmlFor={`launch-${item.key}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                      >
                        {item.label}
                      </label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Setup Guide */}
            <Card>
              <CardHeader>
                <CardTitle>Technical Setup Guide</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { 
                      service: "Supabase", 
                      steps: [
                        "Create project at supabase.com",
                        "Run database migrations",
                        "Configure RLS policies",
                        "Set up auth providers (email, OAuth)"
                      ],
                      enabled: true
                    },
                    { 
                      service: "Resend", 
                      steps: [
                        "Sign up at resend.com",
                        "Verify domain for sending",
                        "Add API key to secrets",
                        "Test transactional emails"
                      ],
                      enabled: true
                    },
                    { 
                      service: "Stripe", 
                      steps: [
                        "Create Stripe account",
                        "Set up products and pricing",
                        "Add webhook endpoint",
                        "Test in dev mode before going live"
                      ],
                      enabled: false
                    },
                    { 
                      service: "Analytics", 
                      steps: [
                        "Choose provider (GA4, Plausible, PostHog)",
                        "Add tracking script to site",
                        "Set up conversion events",
                        "Create dashboard for key metrics"
                      ],
                      enabled: true
                    },
                    { 
                      service: "Edge Functions", 
                      steps: [
                        "Deploy functions to Supabase",
                        "Configure CORS policies",
                        "Set up environment variables",
                        "Test with Postman/curl"
                      ],
                      enabled: true
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          {item.service}
                          {!item.enabled && (
                            <Badge variant="outline" className="text-xs">
                              Optional
                            </Badge>
                          )}
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {item.steps.map((step, sIdx) => (
                          <div key={sIdx} className="flex items-start gap-2 text-sm">
                            <Checkbox id={`setup-${idx}-${sIdx}`} className="mt-0.5" />
                            <label 
                              htmlFor={`setup-${idx}-${sIdx}`}
                              className="text-muted-foreground cursor-pointer"
                            >
                              {step}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Risk Register */}
            <Card>
              <CardHeader>
                <CardTitle>Risk Register - Top 10 Risks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { 
                      risk: "Low initial user acquisition",
                      likelihood: "High",
                      impact: "High",
                      mitigation: "Pre-launch email list, Product Hunt, Reddit seeding, early access program"
                    },
                    { 
                      risk: "Technical debt from moving too fast",
                      likelihood: "Medium",
                      impact: "Medium",
                      mitigation: "Code reviews, testing framework, refactoring sprints every 2 weeks"
                    },
                    { 
                      risk: "Competitor launches similar product",
                      likelihood: "Medium",
                      impact: "High",
                      mitigation: "Focus on unique value prop, build moat through customer relationships"
                    },
                    { 
                      risk: "Scaling issues at 10K+ users",
                      likelihood: "Low",
                      impact: "High",
                      mitigation: "Load testing, DB optimization, caching strategy, CDN for assets"
                    },
                    { 
                      risk: "Customer churn after free trial",
                      likelihood: "High",
                      impact: "High",
                      mitigation: "Onboarding optimization, weekly check-ins, value delivery in first 7 days"
                    },
                    { 
                      risk: "Data breach / security incident",
                      likelihood: "Low",
                      impact: "Critical",
                      mitigation: "SOC2 compliance, penetration testing, bug bounty, security audits"
                    },
                    { 
                      risk: "Key team member leaves",
                      likelihood: "Medium",
                      impact: "Medium",
                      mitigation: "Documentation, knowledge sharing, cross-training, equity incentives"
                    },
                    { 
                      risk: "Payment integration issues",
                      likelihood: "Medium",
                      impact: "High",
                      mitigation: "Thorough testing, fallback payment methods, customer support ready"
                    },
                    { 
                      risk: "Market timing - too early/late",
                      likelihood: "Medium",
                      impact: "High",
                      mitigation: "Customer development, pivot readiness, quarterly market analysis"
                    },
                    { 
                      risk: "Burn rate exceeds runway",
                      likelihood: "Medium",
                      impact: "Critical",
                      mitigation: "Monthly financial review, lean operations, revenue milestones"
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <h4 className="font-semibold text-sm flex-1">
                          {idx + 1}. {item.risk}
                        </h4>
                        <div className="flex gap-2 shrink-0">
                          <Badge 
                            variant={
                              item.likelihood === 'High' ? 'destructive' : 
                              item.likelihood === 'Medium' ? 'default' : 
                              'secondary'
                            }
                            className="text-xs"
                          >
                            L: {item.likelihood}
                          </Badge>
                          <Badge 
                            variant={
                              item.impact === 'Critical' ? 'destructive' :
                              item.impact === 'High' ? 'destructive' : 
                              item.impact === 'Medium' ? 'default' : 
                              'secondary'
                            }
                            className="text-xs"
                          >
                            I: {item.impact}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium">Mitigation:</span> {item.mitigation}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Recommended Tools by Category</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Code Editor */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary" />
                    Code Editor & IDE
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "VS Code",
                        purpose: "Most popular code editor with excellent extension ecosystem",
                        freeTier: "100% free and open source",
                        link: "https://code.visualstudio.com"
                      },
                      {
                        name: "Cursor",
                        purpose: "AI-powered IDE for faster development with code generation",
                        freeTier: "Free tier available, Pro at $20/mo",
                        link: "https://cursor.sh"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Design */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Design & Prototyping
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "Figma",
                        purpose: "Industry-standard design tool for UI/UX, prototyping, and collaboration",
                        freeTier: "Free tier includes 3 Figma and 3 FigJam files",
                        link: "https://figma.com"
                      },
                      {
                        name: "v0.dev",
                        purpose: "AI-powered design to code tool that generates React components",
                        freeTier: "200 free credits/month",
                        link: "https://v0.dev"
                      },
                      {
                        name: "Unsplash",
                        purpose: "High-quality free stock photos for landing pages and marketing",
                        freeTier: "100% free for commercial use",
                        link: "https://unsplash.com"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Email */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Megaphone className="h-4 w-4 text-primary" />
                    Email & Communication
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "Resend",
                        purpose: "Modern email API for transactional emails with great DX",
                        freeTier: "3,000 emails/month free",
                        link: "https://resend.com"
                      },
                      {
                        name: "Mailchimp",
                        purpose: "Marketing automation and newsletter platform",
                        freeTier: "500 contacts and 1,000 sends/month free",
                        link: "https://mailchimp.com"
                      },
                      {
                        name: "ConvertKit",
                        purpose: "Email marketing for creators with landing pages and automation",
                        freeTier: "Free up to 300 subscribers",
                        link: "https://convertkit.com"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Analytics */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-primary" />
                    Analytics & Tracking
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "Plausible",
                        purpose: "Privacy-friendly analytics alternative to Google Analytics",
                        freeTier: "30-day free trial, then $9/mo",
                        link: "https://plausible.io"
                      },
                      {
                        name: "PostHog",
                        purpose: "Product analytics with session replay and feature flags",
                        freeTier: "1M events/month free",
                        link: "https://posthog.com"
                      },
                      {
                        name: "Mixpanel",
                        purpose: "Advanced product analytics and user tracking",
                        freeTier: "20M events/month free",
                        link: "https://mixpanel.com"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Error Tracking */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" />
                    Error Tracking & Monitoring
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "Sentry",
                        purpose: "Error tracking and performance monitoring for full stack apps",
                        freeTier: "5,000 errors/month and 10,000 transactions/month free",
                        link: "https://sentry.io"
                      },
                      {
                        name: "LogRocket",
                        purpose: "Session replay with error tracking to debug user issues",
                        freeTier: "1,000 sessions/month free",
                        link: "https://logrocket.com"
                      },
                      {
                        name: "BetterStack",
                        purpose: "Uptime monitoring and incident management",
                        freeTier: "Free tier with 3 monitors",
                        link: "https://betterstack.com"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Queue/Cron */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-primary" />
                    Background Jobs & Scheduling
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "Supabase Cron (pg_cron)",
                        purpose: "Built-in scheduled jobs using PostgreSQL extension",
                        freeTier: "Included with Supabase",
                        link: "https://supabase.com/docs/guides/database/extensions/pg_cron"
                      },
                      {
                        name: "Trigger.dev",
                        purpose: "Background jobs with retries and monitoring",
                        freeTier: "Free tier with 100 runs/month",
                        link: "https://trigger.dev"
                      },
                      {
                        name: "Inngest",
                        purpose: "Event-driven background jobs and workflows",
                        freeTier: "25K events/month free",
                        link: "https://inngest.com"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Infrastructure */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Infrastructure & Deployment
                  </h3>
                  <div className="space-y-2">
                    {[
                      {
                        name: "Vercel",
                        purpose: "Deploy frontend apps with zero config and global CDN",
                        freeTier: "Hobby plan includes 100GB bandwidth",
                        link: "https://vercel.com"
                      },
                      {
                        name: "Supabase",
                        purpose: "Backend-as-a-service with database, auth, storage, and functions",
                        freeTier: "Free tier includes 500MB database and 50MB storage",
                        link: "https://supabase.com"
                      },
                      {
                        name: "Cloudflare",
                        purpose: "CDN, DNS, and DDoS protection",
                        freeTier: "Free tier includes CDN and SSL",
                        link: "https://cloudflare.com"
                      },
                      {
                        name: "Fly.io",
                        purpose: "Deploy Docker containers globally with edge computing",
                        freeTier: "$5 credit/month (covers small apps)",
                        link: "https://fly.io"
                      }
                    ].map((tool, idx) => (
                      <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="font-medium text-sm mb-1">{tool.name}</h4>
                            <p className="text-xs text-muted-foreground mb-1">{tool.purpose}</p>
                            <p className="text-xs text-primary">{tool.freeTier}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(tool.link, '_blank')}
                            className="shrink-0"
                          >
                            Visit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default IdeaBuilder;
